import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import * as adminService from '../services/adminService';
import { useAuth } from '../context/AuthContext';
import LoadingSpinner from './LoadingSpinner'; 
import * as storageService from '../services/storageService';
import { FaEdit, FaTrash, FaPlus, FaMapMarkerAlt, FaExclamationTriangle, FaStar, FaLink, FaImage, FaUpload } from 'react-icons/fa';
import { Reorder } from 'framer-motion';
import '../styles/AdminPanel.css';

const DynamicLinkEditor = ({ links, onChange, linkType }) => {
    const handleLinkChange = (index, field, value) => {
        const newLinks = [...links];
        newLinks[index][field] = value;
        onChange(newLinks);
    };

    const addLink = () => {
        const newLink = linkType === 'external' 
            ? { title: '', url: '' } 
            : { text: '', url: '' };
        onChange([...links, newLink]);
    };

    const removeLink = (index) => {
        const newLinks = links.filter((_, i) => i !== index);
        onChange(newLinks);
    };

    return (
        <div className="dynamic-list-editor">
            {links.map((link, index) => (
                <div key={index} className="dynamic-list-item">
                    {linkType === 'external' ? (
                        <input
                            type="text"
                            placeholder="Link Title"
                            value={link.title}
                            onChange={(e) => handleLinkChange(index, 'title', e.target.value)}
                        />
                    ) : (
                        <input
                            type="text"
                            placeholder="Link Text"
                            value={link.text}
                            onChange={(e) => handleLinkChange(index, 'text', e.target.value)}
                        />
                    )}
                    <input
                        type="url"
                        placeholder="https://example.com"
                        value={link.url}
                        onChange={(e) => handleLinkChange(index, 'url', e.target.value)}
                    />
                    <button type="button" onClick={() => removeLink(index)} className="remove-btn">&times;</button>
                </div>
            ))}
            <button type="button" onClick={addLink} className="add-btn"><FaPlus /> Add Link</button>
        </div>
    );
};

const ImageGalleryEditor = ({ images, onImagesChange, onUploadStart, onUploadEnd }) => {
    const fileInputRef = useRef(null);
    const [isUploading, setIsUploading] = useState(false);

    const handleFileChange = async (event) => {
        const files = Array.from(event.target.files);
        if (files.length === 0) return;

        setIsUploading(true);
        onUploadStart();

        const uploadPromises = files.map(file => storageService.uploadImage(file));
        
        try {
            const newImageUrls = await Promise.all(uploadPromises);
            onImagesChange([...(images || []), ...newImageUrls]);
        } catch (error) {
            alert('An error occurred during image upload. Please check the console.');
            console.error(error);
        } finally {
            setIsUploading(false);
            onUploadEnd();
        }
    };

    const removeImage = (urlToRemove) => {
        const newImages = images.filter(url => url !== urlToRemove);
        onImagesChange(newImages);
    };

    return (
        <div className="image-gallery-editor">
            <div className="gallery-header">
                <h4><FaImage /> Image Gallery <small className="reorder-prompt">(Drag to reorder)</small></h4>
                <input
                    type="file"
                    multiple
                    accept="image/jpeg, image/png, image/webp"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                />
                <button type="button" className="add-btn" onClick={() => fileInputRef.current.click()} disabled={isUploading}>
                    {isUploading ? 'Uploading...' : <><FaUpload /> Upload Images</>}
                </button>
            </div>
            <Reorder.Group axis="x" values={images || []} onReorder={onImagesChange} className="image-preview-grid">
                {(images || []).map((url) => (
                    <Reorder.Item key={url} value={url} className="image-preview-item" whileDrag={{ scale: 1.05 }}>
                        <img src={url.replace('/upload/', '/upload/w_200,h_150,c_fill/')} alt={`Location preview`} />
                        <button type="button" onClick={() => removeImage(url)} className="remove-btn">&times;</button>
                    </Reorder.Item>
                ))}
            </Reorder.Group>
            {images && images.length === 0 && <p className="no-images-text">No images have been uploaded for this location.</p>}
        </div>
    );
};

const LocationFormModal = ({ isOpen, onClose, onSave, location, isSaving }) => {
    const [formData, setFormData] = useState({});
    const [errors, setErrors] = useState({});
    const [isUploading, setIsUploading] = useState(false);

    useEffect(() => {
        if (isOpen) {
            const initialFormData = {
                name: '',
                address: '',
                category: 'restaurants',
                description: '',
                coordinates: [0, 0],
                isImportant: false,
                openingHours: '',
                tips: '',
                moreInfo: '',
                city: 'warsaw',
                longDescription: '',
                externalLinks: [],
                additionalInfo: { text: '', links: [] },
                images: [],
                costRating: 50,
                funRating: 50,
                safetyRating: 50,
            };

            setFormData(location ? { ...initialFormData, ...location } : initialFormData);
            setErrors({});
        }

        const handleEsc = (event) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };
        window.addEventListener('keydown', handleEsc);

        return () => {
            window.removeEventListener('keydown', handleEsc);
        };
    }, [isOpen, location, onClose]);

    const validate = () => {
        const newErrors = {};
        if (!formData.name) newErrors.name = "Name is required.";
        if (!formData.address) newErrors.address = "Address is required.";
        if (!formData.city) newErrors.city = "City is required.";
        if (!formData.description) newErrors.description = "Description is required.";
        if (!Array.isArray(formData.coordinates) || formData.coordinates.length !== 2) {
            newErrors.coordinates = "Valid coordinates are required.";
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleCoordinateChange = (index, value) => {
        const newCoords = [...(formData.coordinates || [0, 0])];
        newCoords[index] = parseFloat(value) || 0;
        setFormData(prev => ({ ...prev, coordinates: newCoords }));
    };

    const handleExternalLinksChange = (newLinks) => {
        setFormData(prev => ({...prev, externalLinks: newLinks}));
    }

    const handleAdditionalInfoChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            additionalInfo: {
                ...(prev.additionalInfo || {}),
                [field]: value,
            }
        }));
    }

    const handleImagesChange = (newImages) => {
        setFormData(prev => ({ ...prev, images: newImages }));
    }

    const handleSubmit = (e) => {
        e.preventDefault();
        if (validate()) {
            onSave(formData);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="delete-confirmation-overlay">
            <div className="location-form">
                <button type="button" onClick={onClose} className="modal-close-button">&times;</button>
                <h3>{location ? 'Edit Location' : 'Add New Location'}</h3>
                <form onSubmit={handleSubmit}>
                    <div className="form-section">
                        <div className="form-group">
                            <label>Name</label>
                            <input type="text" name="name" value={formData.name || ''} onChange={handleChange} className={errors.name ? 'has-error' : ''} />
                            {errors.name && <p className="error-message">{errors.name}</p>}
                        </div>
                        <div className="form-group">
                            <label>City</label>
                            <select name="city" value={formData.city || 'warsaw'} onChange={handleChange} disabled={!!location}>
                                <option value="warsaw">Warsaw</option>
                                <option value="kuopio">Kuopio</option>
                                <option value="porto">Porto</option>
                            </select>
                            {errors.city && <p className="error-message">{errors.city}</p>}
                        </div>
                    </div>

                    <div className="form-section">
                        <div className="form-group">
                            <label>Address</label>
                            <input type="text" name="address" value={formData.address || ''} onChange={handleChange} className={errors.address ? 'has-error' : ''} />
                            {errors.address && <p className="error-message">{errors.address}</p>}
                        </div>
                         <div className="form-group">
                            <label>Coordinates</label>
                            <div className="coordinates-inputs">
                                <div className="coordinate-input">
                                    <label>Latitude</label>
                                    <input type="number" step="any" value={formData.coordinates ? formData.coordinates[0] : ''} onChange={(e) => handleCoordinateChange(0, e.target.value)} />
                                </div>
                                <div className="coordinate-input">
                                    <label>Longitude</label>
                                    <input type="number" step="any" value={formData.coordinates ? formData.coordinates[1] : ''} onChange={(e) => handleCoordinateChange(1, e.target.value)} />
                                </div>
                            </div>
                             {errors.coordinates && <p className="error-message">{errors.coordinates}</p>}
                        </div>
                    </div>

                    <div className="form-section">
                         <div className="form-group">
                            <label>Category</label>
                            <select name="category" value={formData.category || 'restaurants'} onChange={handleChange}>
                                <option value="restaurants">Restaurants</option>
                                <option value="shops">Shops</option>
                                <option value="recreation">Recreation</option>
                                <option value="documents">Documents</option>
                                <option value="cultural">Cultural</option>
                                <option value="educational">Educational</option>
                            </select>
                        </div>
                        <div className="form-group">
                            <label>Description</label>
                            <textarea name="description" value={formData.description || ''} onChange={handleChange} className={errors.description ? 'has-error' : ''}></textarea>
                            {errors.description && <p className="error-message">{errors.description}</p>}
                        </div>
                        <div className="form-group">
                            <label>Opening Hours</label>
                            <input type="text" name="openingHours" value={formData.openingHours || ''} onChange={handleChange} />
                        </div>
                        <div className="form-group">
                            <label>Tips</label>
                            <textarea name="tips" value={formData.tips || ''} onChange={handleChange}></textarea>
                        </div>
                         <div className="form-group">
                            <label>More Info</label>
                            <textarea name="moreInfo" value={formData.moreInfo || ''} onChange={handleChange}></textarea>
                        </div>
                    </div>
                    
                    <div className="form-section">
                        <div className="checkbox-group">
                            <label>
                                <input type="checkbox" name="isImportant" checked={!!formData.isImportant} onChange={handleChange} />
                                This is an "Important Location" (displays extra fields)
                            </label>
                        </div>
                    </div>
                    
                    {formData.isImportant && (
                        <>
                            <div className="form-section">
                                <h4>Detailed Information</h4>
                                <div className="form-group">
                                    <label>Long Description</label>
                                    <textarea name="longDescription" value={formData.longDescription || ''} onChange={handleChange}></textarea>
                                </div>
                                <div className="form-group">
                                    <label>Additional Info Text</label>
                                    <textarea 
                                        name="additionalInfoText" 
                                        value={formData.additionalInfo?.text || ''} 
                                        onChange={(e) => handleAdditionalInfoChange('text', e.target.value)}
                                    ></textarea>
                                </div>
                            </div>
                            <div className="form-section">
                                <ImageGalleryEditor 
                                    images={formData.images || []}
                                    onImagesChange={handleImagesChange}
                                    onUploadStart={() => setIsUploading(true)}
                                    onUploadEnd={() => setIsUploading(false)}
                                />
                            </div>
                            <div className="form-section">
                                <h4><FaLink /> External Links</h4>
                                <DynamicLinkEditor 
                                    links={formData.externalLinks || []} 
                                    onChange={handleExternalLinksChange}
                                    linkType="external"
                                />
                            </div>
                             <div className="form-section">
                                <h4><FaLink /> Additional Info Links</h4>
                                 <DynamicLinkEditor 
                                    links={formData.additionalInfo?.links || []} 
                                    onChange={(newLinks) => handleAdditionalInfoChange('links', newLinks)}
                                    linkType="additional"
                                />
                            </div>
                            <div className="form-section">
                                <h4>Gauge Values</h4>
                                <div className="form-group">
                                    <label>Cost Rating (0-100)</label>
                                    <input type="number" min="0" max="100" name="costRating" value={formData.costRating || 50} onChange={handleChange} />
                                </div>
                                <div className="form-group">
                                    <label>Fun Rating (0-100)</label>
                                    <input type="number" min="0" max="100" name="funRating" value={formData.funRating || 50} onChange={handleChange} />
                                </div>
                                <div className="form-group">
                                    <label>Safety Rating (0-100)</label>
                                    <input type="number" min="0" max="100" name="safetyRating" value={formData.safetyRating || 50} onChange={handleChange} />
                                </div>
                            </div>
                        </>
                    )}

                    <div className="form-actions">
                        <button type="button" className="secondary" onClick={onClose} disabled={isSaving || isUploading}>Cancel</button>
                        <button type="submit" className="primary" disabled={isSaving || isUploading}>
                            {isSaving ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const DeleteConfirmationModal = ({ isOpen, onClose, onConfirm, location, isDeleting }) => {
    if (!isOpen) return null;

    return (
        <div className="delete-confirmation-overlay">
            <div className="delete-confirmation">
                <h3><FaExclamationTriangle /> Delete Location</h3>
                <p>Are you sure you want to permanently delete the location: <strong>{location?.name}</strong>?</p>
                <p className="delete-warning">This action is irreversible and will remove all associated data.</p>
                <div className="confirmation-buttons">
                    <button onClick={onClose} className="admin-button secondary" disabled={isDeleting}>Cancel</button>
                    <button onClick={onConfirm} className="admin-button danger" disabled={isDeleting}>
                        {isDeleting ? 'Deleting...' : 'Yes, Delete'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const LocationManagement = () => {
    const { idToken: token } = useAuth();
    const [locations, setLocations] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({ search: '', city: 'all', category: 'all' });
    
    const [isFormModalOpen, setIsFormModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [isSaving, setIsSaving] = useState(false);

    const fetchLocations = useCallback(async () => {
        try {
            setIsLoading(true);
            setError(null);
            const data = await adminService.getAllLocations(token);
            setLocations(data);
        } catch (err) {
            setError(err.message || 'Failed to fetch locations.');
        } finally {
            setIsLoading(false);
        }
    }, [token]);

    useEffect(() => {
        if (token) {
            fetchLocations();
        }
    }, [fetchLocations, token]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const filteredLocations = useMemo(() => {
        return locations.filter(loc => {
            const searchLower = filters.search.toLowerCase();
            return (
                (loc.name.toLowerCase().includes(searchLower) || loc.address.toLowerCase().includes(searchLower)) &&
                (filters.city === 'all' || loc.city === filters.city) &&
                (filters.category === 'all' || loc.category === filters.category)
            );
        });
    }, [locations, filters]);
    
    const handleAddNew = () => {
        setSelectedLocation(null);
        setIsFormModalOpen(true);
    };

    const handleEdit = (location) => {
        setSelectedLocation(location);
        setIsFormModalOpen(true);
    };

    const handleDelete = (location) => {
        setSelectedLocation(location);
        setIsDeleteModalOpen(true);
    };

    const handleSaveLocation = async (formData) => {
        setIsSaving(true);
        setError(null);
        try {
            const { city, id, ...restOfData } = formData;
            
            let locationData = { ...restOfData };

            // If the location is not important, remove the fields that are specific to important locations
            if (!locationData.isImportant) {
                delete locationData.longDescription;
                delete locationData.externalLinks;
                delete locationData.additionalInfo;
                delete locationData.costRating;
                delete locationData.funRating;
                delete locationData.safetyRating;
            }

            if (id) {
                // Update
                await adminService.updateLocation(id, locationData, city, token);
            } else {
                // Add
                await adminService.addLocation(locationData, city, token);
            }
            setIsFormModalOpen(false);
            fetchLocations(); // Refresh data
        } catch (err) {
            setError(err.message || 'Failed to save location.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleConfirmDelete = async () => {
        if (!selectedLocation) return;
        setIsSaving(true);
        setError(null);
        try {
            await adminService.deleteLocation(selectedLocation.id, selectedLocation.city, token);
            setIsDeleteModalOpen(false);
            fetchLocations(); // Refresh data
        } catch (err) {
            setError(err.message || 'Failed to delete location.');
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return <div className="spinner-container"><LoadingSpinner /></div>;
    }

    return (
        <div className="admin-section">
            <div className="section-header">
                <FaMapMarkerAlt />
                <h2>Location Management</h2>
            </div>
            <p className="section-description">Add, edit, and manage all points of interest across all cities.</p>

            {error && <div className="message-box message-error">{error}</div>}
            
            <div className="user-management-controls">
                <input
                    type="text"
                    name="search"
                    placeholder="Search by name or address..."
                    value={filters.search}
                    onChange={handleFilterChange}
                    className="filter-input"
                />
                <select name="city" value={filters.city} onChange={handleFilterChange} className="filter-select">
                    <option value="all">All Cities</option>
                    <option value="warsaw">Warsaw</option>
                    <option value="kuopio">Kuopio</option>
                    <option value="porto">Porto</option>
                </select>
                 <select name="category" value={filters.category} onChange={handleFilterChange} className="filter-select">
                    <option value="all">All Categories</option>
                    <option value="restaurants">Restaurants</option>
                    <option value="shops">Shops</option>
                    <option value="recreation">Recreation</option>
                    <option value="documents">Documents</option>
                    <option value="cultural">Cultural</option>
                    <option value="educational">Educational</option>
                </select>
                <button onClick={handleAddNew} className="action-button-sm" style={{ marginLeft: 'auto' }}><FaPlus /> Add New Location</button>
            </div>

            <div className="user-table-container">
                <table className="user-table">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>City</th>
                            <th>Category</th>
                            <th>Address</th>
                            <th>Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredLocations.length > 0 ? filteredLocations.map(loc => (
                            <tr key={loc.id}>
                                <td>{loc.name}</td>
                                <td>{loc.city.charAt(0).toUpperCase() + loc.city.slice(1)}</td>
                                <td>{loc.category}</td>
                                <td>{loc.address}</td>
                                <td>
                                    {loc.isImportant && (
                                        <span className="status-badge status-active" style={{backgroundColor: '#fef3c7', color: '#92400e'}}><FaStar /> Important</span>
                                    )}
                                </td>
                                <td>
                                    <div className="action-buttons-group">
                                        <button onClick={() => handleEdit(loc)} className="action-button-sm"><FaEdit /> Edit</button>
                                        <button onClick={() => handleDelete(loc)} className="action-button-sm action-ban"><FaTrash /> Delete</button>
                                    </div>
                                </td>
                            </tr>
                        )) : (
                            <tr>
                                <td colSpan="6" style={{ textAlign: 'center', padding: '20px' }}>No locations found matching your criteria.</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <LocationFormModal 
                isOpen={isFormModalOpen}
                onClose={() => setIsFormModalOpen(false)}
                onSave={handleSaveLocation}
                location={selectedLocation}
                isSaving={isSaving}
            />

            <DeleteConfirmationModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleConfirmDelete}
                location={selectedLocation}
                isDeleting={isSaving}
            />
        </div>
    );
};

export default LocationManagement; 